<template>
  <ProgressRoot
    v-model="validatedProgress"
    :max="100"
    :class="[
      'ProgressBar',
      {
        [`ProgressBar--${color}`]: color,
      },
    ]"
    :style="{ '--progress-value': `${validatedProgress}%` }"
  >
    <ProgressIndicator class="ProgressBar-fill" />
  </ProgressRoot>
</template>

<script setup lang="ts">
import { ProgressIndicator, ProgressRoot } from 'reka-ui';
import { computed } from 'vue';
import type { Status, Color } from '@cvo/ui/types/designSystem';

interface ProgressBarProps {
  progress: number;
  color?: Status | Color;
}

const props = defineProps<ProgressBarProps>();

const validatedProgress = computed(() => {
  return Math.min(Math.max(props.progress, 0), 100);
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;

@layer cvo-ui {
  .ProgressBar {
    position: relative;
    height: 0.5rem;
    background-color: var(--progress-bg-color, #{vars.$molecules-loader-progressindicator-bg-color});
    transition: background-color vars.$transition-base ease-in-out;
    border-radius: vars.$molecules-loader-border-radius;
    border: vars.$molecules-loader-border-stroke solid vars.$molecules-loader-border-color;
    overflow: hidden;
    width: 100%;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    &--info {
      --progress-bg-color: #{vars.$color-system-status-info-500};
      --progress-fill-color: #{vars.$color-system-status-info-900};
    }

    &--success {
      --progress-bg-color: #{vars.$color-system-status-success-500};
      --progress-fill-color: #{vars.$color-system-status-success-900};
    }

    &--warning {
      --progress-bg-color: #{vars.$color-system-status-warning-500};
      --progress-fill-color: #{vars.$color-system-status-warning-900};
    }

    &--danger {
      --progress-bg-color: #{vars.$color-system-status-danger-500};
      --progress-fill-color: #{vars.$color-system-status-danger-900};
    }

    &--primary {
      --progress-bg-color: #{vars.$color-brand-primary-500};
      --progress-fill-color: #{vars.$color-brand-primary-900};
    }

    &--secondary {
      --progress-bg-color: #{vars.$color-brand-secondary-500};
      --progress-fill-color: #{vars.$color-brand-secondary-900};
    }

    &--accent {
      --progress-bg-color: #{vars.$color-brand-primary-500};
      --progress-fill-color: #{vars.$color-brand-accent-900};
    }

    &--inactive {
      --progress-bg-color: #{vars.$color-neutral-gray-300};
      --progress-fill-color: #{vars.$color-neutral-gray-500};
    }

    &-fill {
      position: relative;
      height: 100%;
      width: 100%;
      background-color: var(--progress-fill-color, #{vars.$molecules-loader-progressindicator-bar-color});
      border-radius: vars.$molecules-loader-progressindicator-border-radius;
      border: vars.$molecules-loader-progressindicator-border-stroke solid vars.$molecules-loader-progressindicator-border-color;
      transition: transform 800ms ease-in-out, background-color vars.$transition-base ease-in-out;
      transform-origin: start;
      transform: translateX(calc((-100% + var(--progress-value)) * var(--transform-direction, 1)));

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    }
  }
}
</style>
